﻿import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Backdrop, CircularProgress } from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import {
  TApiApplicationDocument,
  TSignatureValidationState,
  TUseLocalFilesResult,
} from '~/modules/covenants/types.ts';
import { FetchStatus } from '~/store/covenants/types.ts';
import {
  selectApplicationDocuments,
  selectApplicationDocumentsPageConfiguration,
  selectApplicationUnsignedDocumentsCount,
  selectApplicationDocumentsStatus,
  selectCurrentApplication,
  selectCurrentApplicationStatus,
  selectCurrentContragent,
} from '~/store/covenants/selectors.ts';
import { useStatusModal } from '~/modules/covenants/CovenantsDataApplicationForm/hooks/useStatusModal.ts';
import { useLocalFiles } from '~/modules/covenants/CovenantsDataApplicationForm/hooks/useLocalFiles.ts';
import { useCovenantsSigning } from '~/modules/covenants/CovenantsDataApplicationForm/hooks/useCovenantsSigning.ts';
import { useCovenantsApplicationActions } from '~/modules/covenants/CovenantsDataApplicationForm/hooks/useCovenantsApplicationActions.ts';
import { useCovenantsApplicationContragent } from '~/modules/covenants/CovenantsDataApplicationForm/hooks/useCovenantsApplicationContragent.ts';
import { useCovenantsApplicationInitialization } from '~/modules/covenants/CovenantsDataApplicationForm/hooks/useCovenantsApplicationInitialization.ts';
import { useApplicationDocumentRows } from '~/modules/covenants/CovenantsDataApplicationForm/hooks/useApplicationDocumentRows.ts';
import { useDocumentUploadLoader } from '~/modules/covenants/CovenantsDataApplicationForm/hooks/useDocumentUploadLoader.ts';
import { useCovenantsApplicationRefresh } from '~/modules/covenants/CovenantsDataApplicationForm/hooks/useCovenantsApplicationRefresh.ts';
import { useBulkSignClick } from '~/modules/covenants/CovenantsDataApplicationForm/hooks/useBulkSignClick.ts';
import { useCovenantsSigningState } from '~/modules/covenants/CovenantsDataApplicationForm/hooks/useCovenantsSigningState.ts';
import { ApplicationFormView } from '~/modules/covenants/CovenantsDataApplicationForm/CovenantsApplicationForm.tsx';
import { ElectronicSignature } from '~/components/SignatureVerification/ElectronicSignature/ElectronicSignature.tsx';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader.tsx';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import SuccessModal from '~/components/modals/SuccessModal/SuccessModal.tsx';
import {
  ALLOWED_UPLOAD_FILE_EXTENSIONS,
  COVENANTS_GET_STARTED_PATH,
  DEFAULT_DOCUMENTS_PAGE,
  DEFAULT_DOCUMENTS_ROWS_PER_PAGE,
  UNSUPPORTED_FILE_EXTENSION_ERROR_MESSAGE,
} from '~/modules/covenants/constants.tsx';
import { DIIA_DOCUMENTS_LIMIT_MESSAGE } from '~/modules/covenants/CovenantsDataApplicationForm/constants.ts';
import {
  buildApplicationSubmittedModal,
  buildCovenantsApplicationPath,
  buildApplicationVersionMismatchModal,
  buildSignatureValidationState,
  buildNoDocumentsModal,
  buildNoFilesModal,
  buildSignatureQrTimeoutModal,
  buildStatusErrorModal,
  persistCovenantsApplicationContragent,
} from '~/modules/covenants/utils.ts';
import { RootState } from '~/store/store.ts';
import {
  buildSignatureValidationContent,
  clearSignatureValidationStateForDocument,
  getDocumentPreviewPayload,
  getMaxDocumentsPage,
} from '~/modules/covenants/CovenantsDataApplicationForm/utils.ts';
import { useApplicationUnsignedDocumentsCount } from '~/modules/covenants/CovenantsDataApplicationForm/hooks/useApplicationUnsignedDocumentsCount.ts';

const ApplicationFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { applicationId } = useParams<{ applicationId: string }>();

  const contragent = useSelector(selectCurrentContragent);
  const application = useSelector(selectCurrentApplication);
  const applicationDocuments = useSelector(selectApplicationDocuments);
  const applicationDocumentsPageConfiguration = useSelector(
    selectApplicationDocumentsPageConfiguration
  );
  const applicationUnsignedDocumentsCount = useSelector(selectApplicationUnsignedDocumentsCount);
  const documentsStatus = useSelector(selectApplicationDocumentsStatus);
  const applicationStatus = useSelector(selectCurrentApplicationStatus);
  const iitHashes = useSelector((state: RootState) => state.iit.IITHashes);
  const { isLoading: isIITLoading } = useSelector((state: RootState) => state.iit) || {};
  const [documentsPage, setDocumentsPage] = useState(DEFAULT_DOCUMENTS_PAGE);
  const [documentsRowsPerPage, setDocumentsRowsPerPage] = useState(DEFAULT_DOCUMENTS_ROWS_PER_PAGE);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSignatureFlowLoading, setIsSignatureFlowLoading] = useState(false);
  const [isDiiaSigningStarted, setIsDiiaSigningStarted] = useState(false);
  const [signatureFlowDocuments, setSignatureFlowDocuments] = useState<TApiApplicationDocument[]>(
    []
  );
  const [deepLink, setDeepLink] = useState('');
  const [signatureResetCounter, setSignatureResetCounter] = useState(0);
  const [signatureValidationState, setSignatureValidationState] =
    useState<TSignatureValidationState>({});
  const timerRef = useRef<{ resetTimer: () => void; startTimer: () => void }>(null);

  const isInitialDocumentsLoading =
    documentsStatus === FetchStatus.Loading &&
    !applicationDocuments.length &&
    applicationDocumentsPageConfiguration.totalElements === 0;
  const isLoading =
    (!application && applicationStatus === FetchStatus.Loading) || isInitialDocumentsLoading;

  const currentContragentId = useCovenantsApplicationContragent({
    applicationId,
    contragentId: contragent?.id,
  });
  const contragentApplicationsPath = currentContragentId
    ? `/covenants/contragents/${currentContragentId}/my`
    : COVENANTS_GET_STARTED_PATH;

  const { statusModal, setStatusModal, closeStatusModal } = useStatusModal();
  const { documentRows, visibleDocumentRows, handleRemoveVirtualRow } = useApplicationDocumentRows({
    applicationId,
    applicationDocuments,
  });
  const hasApplicationDocuments =
    documentRows.length > 0 || applicationDocumentsPageConfiguration.totalElements > 0;
  const {
    isDocumentUploadLoading,
    handleDocumentUploadStart,
    handleDocumentUploadQueued,
    handleDocumentUploadError,
  } = useDocumentUploadLoader(documentRows);
  const { refreshApplicationData, refreshDocumentList } = useCovenantsApplicationRefresh({
    applicationId,
    application,
    contragentId: currentContragentId,
    documentsPage,
    documentsRowsPerPage,
  });
  const handleSignatureUploadSuccess = useCallback((doc: TApiApplicationDocument) => {
    setSignatureValidationState((prevState) =>
      clearSignatureValidationStateForDocument(prevState, doc)
    );
  }, []);

  const localFilesController: TUseLocalFilesResult = useLocalFiles({
    contragentId: currentContragentId,
    applicationId: applicationId || '',
    applicationVersion: application?.version ?? 0,
    applicationDocuments,
    documentRows,
    onVersionMismatch: () =>
      setStatusModal(
        buildApplicationVersionMismatchModal(() => {
          closeStatusModal();
          void refreshApplicationData();
        })
      ),
    onDocumentsRefresh: refreshDocumentList,
    onVirtualRowConsumed: handleRemoveVirtualRow,
    onDocumentUploadStart: handleDocumentUploadStart,
    onDocumentUploadQueued: handleDocumentUploadQueued,
    onDocumentUploadError: handleDocumentUploadError,
    onSignatureActionStart: handleDocumentUploadStart,
    onSignatureActionQueued: handleDocumentUploadQueued,
    onSignatureActionError: handleDocumentUploadError,
    onSignatureUploadSuccess: handleSignatureUploadSuccess,
    allowedUploadFileExtensions: ALLOWED_UPLOAD_FILE_EXTENSIONS,
    onUnsupportedFileExtension: () =>
      setStatusModal(buildStatusErrorModal(UNSUPPORTED_FILE_EXTENSION_ERROR_MESSAGE)),
  });

  const { localFiles, signatureError, setSignatureError } = localFilesController;

  const {
    hasAnyFiles,
    needsSign,
    needsSignByLoadedRows,
    diiaSignableDocuments,
    isDiiaDocumentsLimitExceeded,
    diiaDisabledMessage,
    documentsForSigning,
    isSignatureInteractionBlocked,
    overlayLoaderSx,
  } = useCovenantsSigningState({
    documentRows,
    signatureFlowDocuments,
    localFiles,
    signatureValidationState,
    iitHashes,
    applicationUnsignedDocumentsCount,
    isDiiaSigningStarted,
    isSignatureFlowLoading,
    isIITLoading,
    isDocumentUploadLoading,
  });

  const {
    handleSubmitCurrentApplication,
    handleRecreateFromDenied,
    handlePreviewDocument,
    handleDownloadDocument,
  } =
    useCovenantsApplicationActions({
      applicationId,
      contragentId: currentContragentId,
      applicationStatus: application?.status,
      isSubmitting,
      documentsPage,
      documentsRowsPerPage,
      setIsSubmitting,
      setIsSignatureModalOpen,
      onError: (message) => {
        setIsDiiaSigningStarted(false);
        setIsSignatureFlowLoading(false);
        closeSignatureModal(true);
        setStatusModal(buildStatusErrorModal(message));
      },
      onValidationError: (response, refreshedDocuments) => {
        setIsSignatureFlowLoading(false);
        closeSignatureModal(true);
        setSignatureValidationState(
          buildSignatureValidationState(response, refreshedDocuments ?? applicationDocuments)
        );
        setStatusModal(buildStatusErrorModal(buildSignatureValidationContent(response)));
      },
      onSuccess: () => {
        setIsSignatureFlowLoading(false);
        closeSignatureModal(true);
        setSignatureValidationState({});
        setStatusModal(buildApplicationSubmittedModal(() => navigate(contragentApplicationsPath)));
      },
      onRecreateSuccess: (newApplicationId) => {
        persistCovenantsApplicationContragent(newApplicationId, currentContragentId);
        navigate(buildCovenantsApplicationPath(newApplicationId, currentContragentId), {
          state: { contragentId: currentContragentId },
        });
      },
    });

  const handleCovenantsSigningSuccess = useCallback(async () => {
    if (isSubmitting || !applicationId || !currentContragentId) {
      setIsSignatureFlowLoading(false);
      return;
    }

    closeSignatureModal(true);
    setIsSignatureFlowLoading(true);
    await handleSubmitCurrentApplication();
  }, [applicationId, currentContragentId, handleSubmitCurrentApplication, isSubmitting]);

  const handleCovenantsSigningError = useCallback(
    (msg: string) => {
      setIsSignatureFlowLoading(false);
      closeSignatureModal(true);
      setStatusModal(buildStatusErrorModal(msg));
    },
    [setStatusModal]
  );

  const handleCovenantsSigningReset = useCallback(() => {
    setIsDiiaSigningStarted(false);
    timerRef.current?.resetTimer();
    setSignatureResetCounter((prev) => prev + 1);
  }, []);

  const { handleIITSignature, onIITSign, handleDiiaSignature, resetSigningSession } =
    useCovenantsSigning({
      contragentId: currentContragentId,
      applicationId: applicationId!,
      documents: diiaSignableDocuments,
      onSuccess: handleCovenantsSigningSuccess,
      onError: handleCovenantsSigningError,
      setDeepLink,
      onDiiaInitialize: () => timerRef.current?.startTimer(),
      onDiiaSigningStarted: () => {
        setIsDiiaSigningStarted(true);
      },
      onReset: handleCovenantsSigningReset,
    });

  function resetSignatureFlow() {
    resetSigningSession();
  }

  const closeSignatureModal = (force = false) => {
    if (!force && (isSignatureFlowLoading || isIITLoading)) {
      return;
    }

    setIsDiiaSigningStarted(false);
    setIsSignatureFlowLoading(false);
    resetSignatureFlow();
    setSignatureFlowDocuments([]);
    setIsSignatureModalOpen(false);
  };

  const { handleBulkSignClick } = useBulkSignClick({
    isSignatureInteractionBlocked,
    isSubmitting,
    applicationId,
    contragentId: currentContragentId,
    applicationStatus: application?.status,
    documentRows,
    documentsRowsPerPage,
    localFiles,
    onSignatureFlowLoadingChange: setIsSignatureFlowLoading,
    onSignatureFlowDocumentsChange: setSignatureFlowDocuments,
    onNoFiles: () => setStatusModal(buildNoFilesModal()),
    onNoUnsignedDocuments: handleSubmitCurrentApplication,
    onOpenSignatureModal: () => setIsSignatureModalOpen(true),
  });
  const { getUnsignedDocumentsCount } = useApplicationUnsignedDocumentsCount({
    contragentId: currentContragentId,
  });

  const handleSubmitOrOpenSigningFlow = async () => {
    if (isSubmitting || isSignatureInteractionBlocked || !applicationId || !currentContragentId) {
      return;
    }

    if (!hasApplicationDocuments) {
      setStatusModal(buildNoDocumentsModal());
      return;
    }

    const unsignedDocumentsCount = await getUnsignedDocumentsCount(applicationId);
    const shouldOpenSigningFlow =
      typeof unsignedDocumentsCount === 'number'
        ? unsignedDocumentsCount > 0
        : needsSignByLoadedRows;

    if (shouldOpenSigningFlow) {
      await handleBulkSignClick({ skipUnsignedCountFetch: true });
      return;
    }

    await handleSubmitCurrentApplication();
  };

  const handleDocumentsRowsPerPageChange = useCallback((nextRowsPerPage: number) => {
    setDocumentsRowsPerPage(nextRowsPerPage);
    setDocumentsPage(DEFAULT_DOCUMENTS_PAGE);
  }, []);
  const handleNavigateToList = useCallback(
    () => navigate(contragentApplicationsPath),
    [navigate, contragentApplicationsPath]
  );

  const handleStatusModalClose = () => {
    closeStatusModal();
    statusModal.onCloseCallback?.();
  };

  const handleStatusModalAction = () => {
    if (statusModal.onClick) {
      statusModal.onClick();
      return;
    }

    handleStatusModalClose();
  };

  const handleQrTimeout = () => {
    setIsDiiaSigningStarted(false);
    setIsSignatureFlowLoading(false);
    closeSignatureModal(true);
    setStatusModal(buildSignatureQrTimeoutModal());
  };

  const onDiiaClick = () => {
    if (isLoading || isSubmitting || isSignatureInteractionBlocked) {
      return false;
    }

    if (isDiiaDocumentsLimitExceeded) {
      setStatusModal(buildStatusErrorModal(diiaDisabledMessage || DIIA_DOCUMENTS_LIMIT_MESSAGE));
      return false;
    }

    setDeepLink('');
    return handleDiiaSignature();
  };

  const signaturePreviewDocuments =
    signatureFlowDocuments.length > 0 ? signatureFlowDocuments : documentRows;

  const handleSignaturePreview = async (id: number | string) => {
    const previewId =
      documentsForSigning.find((document) =>
        [String(document.idDoc), document.id].includes(String(id))
      )?.id ?? id;
    const previewPayload = getDocumentPreviewPayload(
      signaturePreviewDocuments,
      previewId,
      localFiles
    );

    if (previewPayload) {
      await handlePreviewDocument(previewPayload.document, previewPayload.localFile);
    }
  };

  useEffect(() => {
    if (!applicationId) {
      return;
    }

    setDocumentsPage(DEFAULT_DOCUMENTS_PAGE);
    setDocumentsRowsPerPage(DEFAULT_DOCUMENTS_ROWS_PER_PAGE);
    setSignatureValidationState({});
    setSignatureFlowDocuments([]);
  }, [applicationId]);

  useEffect(() => {
    if (!application?.id || !currentContragentId) {
      return;
    }

    void (async () => {
      await refreshDocumentList();
    })();
  }, [
    application?.id,
    application?.status,
    currentContragentId,
    documentsPage,
    documentsRowsPerPage,
  ]);

  useEffect(() => {
    const maxPage = getMaxDocumentsPage(
      applicationDocumentsPageConfiguration.totalElements,
      documentsRowsPerPage
    );

    if (documentsPage > maxPage) {
      setDocumentsPage(maxPage);
    }
  }, [applicationDocumentsPageConfiguration.totalElements, documentsPage, documentsRowsPerPage]);

  useCovenantsApplicationInitialization({
    applicationId,
    contragentId: currentContragentId,
  });

  if (!applicationId || !currentContragentId) {
    return (
      <ModalAlert
        isOpen
        text="Не вдалося відновити заявку після оновлення сторінки. Відкрийте її повторно зі списку заявок."
        buttonValue="До списку контрагентів"
        onClick={() => navigate(COVENANTS_GET_STARTED_PATH)}
        onClose={() => navigate(COVENANTS_GET_STARTED_PATH)}
      />
    );
  }

  return (
    <>
      <ApplicationFormView
        contragentId={currentContragentId}
        application={application}
        isSubmitting={isSubmitting}
        applicationDocuments={documentRows}
        visibleApplicationDocuments={visibleDocumentRows}
        documentsPage={documentsPage}
        documentsRowsPerPage={documentsRowsPerPage}
        documentsPageConfiguration={applicationDocumentsPageConfiguration}
        signatureValidationState={signatureValidationState}
        localFilesController={localFilesController}
        hasApplicationDocuments={hasApplicationDocuments}
        hasAnyFiles={hasAnyFiles}
        needsSign={needsSign}
        onPreviewDocument={handlePreviewDocument}
        onDownloadDocument={handleDownloadDocument}
        onDocumentsPageChange={setDocumentsPage}
        onDocumentsRowsPerPageChange={handleDocumentsRowsPerPageChange}
        onBulkSignClick={handleBulkSignClick}
        onNoDocuments={() => setStatusModal(buildNoDocumentsModal())}
        onSubmitApplication={handleSubmitOrOpenSigningFlow}
        onNavigateToList={handleNavigateToList}
        onRecreateFromDenied={handleRecreateFromDenied}
      />

      <ElectronicSignature
        isModalOpen={isSignatureModalOpen}
        handleCloseModal={closeSignatureModal}
        handleSignatureDiia={onDiiaClick}
        handleSignatureIIT={handleIITSignature}
        onIITSign={onIITSign}
        handleTimeUp={handleQrTimeout}
        handlePreview={handleSignaturePreview}
        deepLink={deepLink}
        documents={documentsForSigning}
        ref={timerRef}
        waitForDiiaInitialization={true}
        resetCounter={signatureResetCounter}
        isDiiaSignatureDisabled={
          isSignatureInteractionBlocked || isSubmitting || isDiiaDocumentsLimitExceeded
        }
        diiaDisabledMessage={diiaDisabledMessage}
        isIITSignatureDisabled={isSignatureInteractionBlocked || isSubmitting}
        enableIITDocumentsPagination
        iitDocumentsPerPage={documentsRowsPerPage}
      />

      <SuccessModal
        isOpen={statusModal.open && statusModal.type === 'success'}
        text={statusModal.message}
        onClose={handleStatusModalClose}
        onClick={handleStatusModalClose}
        buttonValue="Закрити"
      />

      <ModalAlert
        isOpen={(statusModal.open && statusModal.type === 'error') || !!signatureError}
        text={signatureError ?? statusModal.message}
        buttonValue={signatureError ? undefined : statusModal.buttonValue}
        onClick={signatureError ? undefined : handleStatusModalAction}
        onClose={signatureError ? () => setSignatureError(null) : handleStatusModalClose}
      />

      {(isDiiaSigningStarted || isSignatureFlowLoading || isIITLoading) && <FullPageLoader />}

      <Backdrop open={isDocumentUploadLoading} sx={overlayLoaderSx}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default ApplicationFormPage;
